import { zodResolver } from "@hookform/resolvers/zod";
import { TRPCClientError } from "@trpc/client";
import { Loader2Icon, PlusIcon, TrashIcon } from "lucide-react";
import { useEffect } from "react";
import { Control, useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";

import { Column, Row } from "@/components";
import { FormDivider } from "@/components/form/FormDivider";
import { FormSection } from "@/components/form/FormSection";
import { useErrorToast } from "@/hooks/useErrorToast";
import { useParseDemoConfigFromJobUrl } from "@/hooks/mutations/useParseDemoConfigFromJobUrl";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import {
  demoRegionOptions,
  demoPositionOptions,
  demoRegionCompanies,
} from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import {
  CreateOrUpdateDemoConfigRequest,
  DemoConfig,
  DemoPosition,
  DemoRegion,
} from "@/types";

const filterQuestionSectionSchema = z.object({
  title: z.string().min(1, "Required"),
  questions: z
    .array(
      z.object({
        question: z.string().min(1, "Required"),
      }),
    )
    .min(1, "At least one question is required"),
});

const stevieConfigSchema = z.object({
  client: z.string().min(1, "Required"),
  jobTitle: z.string().min(1, "Required"),
  jobDescription: z.string().min(1, "Required"),
  filterQuestionSections: z
    .array(filterQuestionSectionSchema)
    .min(1, "At least one question section is required"),
  openEndedQuestion: z.string().min(1, "Required"),
});

const demoConfigFormSchema = z.object({
  title: z.string().min(1, "Required"),
  config: z.object({
    autoFillJobUrl: z.string(),
    region: z.enum(Object.values(DemoRegion) as [string, ...string[]], {
      message: "Required",
    }),
    position: z.enum(Object.values(DemoPosition) as [string, ...string[]], {
      message: "Required",
    }),
    stevie: stevieConfigSchema,
    hideCandidatesForCompanies: z.array(z.string()),
  }),
});

type DemoConfigFormData = z.infer<typeof demoConfigFormSchema>;

const FilterQuestionSectionInput = ({
  canRemove,
  control,
  onRemove,
  sectionIndex,
}: {
  canRemove: boolean;
  control: Control<DemoConfigFormData>;
  onRemove: () => void;
  sectionIndex: number;
}) => {
  const {
    append: appendQuestion,
    fields: questionFields,
    remove: removeQuestion,
  } = useFieldArray({
    control,
    name: `config.stevie.filterQuestionSections.${sectionIndex.toString()}.questions` as `config.stevie.filterQuestionSections.${number}.questions`,
  });

  return (
    <Column className="gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
      <Row className="items-start justify-between gap-4">
        <FormField
          control={control}
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          name={`config.stevie.filterQuestionSections.${sectionIndex}.title`}
          render={({ field }) => (
            <FormItem className="flex-1 text-slate-700">
              <FormLabel>Section Title</FormLabel>
              <FormControl>
                <Input placeholder="Section title" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          disabled={!canRemove}
          onClick={() => {
            mixpanelUtils.trackButtonClick(
              "Remove section",
              MIXPANEL_SCREENS.DEMO_CONFIGS,
            );
            onRemove();
          }}
          type="button"
          variant="ghost"
        >
          <TrashIcon className="h-4 w-4" />
        </Button>
      </Row>
      <Column className="gap-2">
        <Row className="items-center justify-between">
          <FormLabel className="text-slate-700">Questions</FormLabel>
          <Button
            onClick={() => {
              mixpanelUtils.trackButtonClick(
                "Add question",
                MIXPANEL_SCREENS.DEMO_CONFIGS,
              );
              appendQuestion({ question: "" });
            }}
            type="button"
            variant="outline"
            size="sm"
          >
            <PlusIcon className="h-4 w-4" />
            Add question
          </Button>
        </Row>
        {questionFields.map((question, questionIndex) => (
          <Row key={question.id} className="items-start gap-2">
            <FormField
              control={control}
              // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
              name={`config.stevie.filterQuestionSections.${sectionIndex}.questions.${questionIndex}.question`}
              render={({ field }) => (
                <FormItem className="flex-1 text-slate-700">
                  <FormControl>
                    <Input placeholder="Question" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              disabled={questionFields.length <= 1}
              onClick={() => {
                mixpanelUtils.trackButtonClick(
                  "Remove question",
                  MIXPANEL_SCREENS.DEMO_CONFIGS,
                );
                removeQuestion(questionIndex);
              }}
              type="button"
              variant="ghost"
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          </Row>
        ))}
      </Column>
    </Column>
  );
};

export const DemoConfigsForm = ({
  demoConfig,
  isSubmitting,
  onSubmit,
  submitButtonText,
}: {
  demoConfig?: DemoConfig;
  isSubmitting: boolean;
  onSubmit: (data: CreateOrUpdateDemoConfigRequest) => void;
  submitButtonText: string;
}) => {
  const form = useForm<DemoConfigFormData>({
    resolver: zodResolver(demoConfigFormSchema),
    defaultValues: {
      title: demoConfig?.title ?? "",
      config: {
        autoFillJobUrl: demoConfig?.config.autoFillJobUrl ?? "",
        region: demoConfig?.config.region ?? "",
        position: demoConfig?.config.position ?? "",
        stevie: {
          client: demoConfig?.config.stevie.client ?? "",
          jobTitle: demoConfig?.config.stevie.jobTitle ?? "",
          jobDescription: demoConfig?.config.stevie.jobDescription ?? "",
          filterQuestionSections:
            demoConfig?.config.stevie.filterQuestionSections.map((section) => ({
              title: section.title,
              questions: section.questions.map((question) => ({
                question,
              })),
            })) ?? [{ title: "", questions: [{ question: "" }] }],
          openEndedQuestion: demoConfig?.config.stevie.openEndedQuestion ?? "",
        },
        hideCandidatesForCompanies:
          demoConfig?.config.hideCandidatesForCompanies ?? [],
      },
    },
  });

  const {
    fields: filterQuestionSectionFields,
    append: appendFilterQuestionSection,
    remove: removeFilterQuestionSection,
  } = useFieldArray({
    control: form.control,
    name: "config.stevie.filterQuestionSections",
  });

  const region = form.watch("config.region");
  const companyChoices = region
    ? // TODO: Improve this.
      demoRegionCompanies[region as DemoRegion]
    : [];
  const isRegionSelected = !!region;

  // Clear or filter `hideCandidatesForCompanies` when region changes.
  useEffect(() => {
    const currentHiddenCompanies = form.getValues(
      "config.hideCandidatesForCompanies",
    );
    if (!region) {
      form.setValue("config.hideCandidatesForCompanies", []);
    } else {
      const validCompanyValues = demoRegionCompanies[region as DemoRegion].map(
        (c) => c.value,
      );
      const filteredCompanies = currentHiddenCompanies.filter((company) =>
        validCompanyValues.includes(company),
      );
      form.setValue("config.hideCandidatesForCompanies", filteredCompanies);
    }
  }, [form, region]);

  const parseMutation = useParseDemoConfigFromJobUrl();
  const errorToast = useErrorToast();
  const autoFillJobUrl = form.watch("config.autoFillJobUrl");

  // Only fill a field when the user hasn't already entered/edited it.
  const fillIfEmpty = (
    name: Parameters<typeof form.setValue>[0],
    value: string,
  ) => {
    const currentValue = form.getValues(name);
    if (!currentValue && !form.getFieldState(name).isDirty) {
      form.setValue(name, value);
    }
  };

  const handleAutoParse = async () => {
    mixpanelUtils.trackButtonClick("Autofill", MIXPANEL_SCREENS.DEMO_CONFIGS);

    let parsed;
    try {
      parsed = await parseMutation.mutateAsync({ autoFillJobUrl });
    } catch (error) {
      const data =
        error instanceof TRPCClientError
          ? (error.data as { code?: string } | null)
          : null;
      const code = data?.code ?? "";
      if (code === "BAD_REQUEST") {
        form.setError("config.autoFillJobUrl", {
          message: "Enter a valid https:// URL.",
        });
      } else if (code === "UNPROCESSABLE_CONTENT") {
        errorToast({
          description:
            "Couldn't autofill from that URL — please fill manually.",
        });
      } else {
        errorToast({
          description: "Something went wrong autofilling. Please try again.",
        });
      }
      return;
    }

    fillIfEmpty("config.stevie.client", parsed.stevie.client);
    fillIfEmpty("config.stevie.jobTitle", parsed.stevie.jobTitle);
    fillIfEmpty("config.stevie.jobDescription", parsed.stevie.jobDescription);
    fillIfEmpty(
      "config.stevie.openEndedQuestion",
      parsed.stevie.openEndedQuestion,
    );

    // Region/position come back as best-effort slugs; only set them when they
    // map to a known enum value and the field is still empty.
    if (
      (Object.values(DemoRegion) as string[]).includes(parsed.region) &&
      !form.getValues("config.region")
    ) {
      form.setValue("config.region", parsed.region);
    }

    if (
      (Object.values(DemoPosition) as string[]).includes(parsed.position) &&
      !form.getValues("config.position")
    ) {
      form.setValue("config.position", parsed.position);
    }

    // Filter question sections: only replace the untouched default section.
    const currentSections = form.getValues(
      "config.stevie.filterQuestionSections",
    );
    const isDefaultSections =
      currentSections.length === 1 &&
      currentSections[0].title === "" &&
      currentSections[0].questions.length === 1 &&
      currentSections[0].questions[0].question === "";
    if (isDefaultSections && parsed.stevie.filterQuestionSections.length > 0) {
      form.setValue(
        "config.stevie.filterQuestionSections",
        parsed.stevie.filterQuestionSections.map((section) => ({
          title: section.title,
          questions: section.questions.map((question) => ({ question })),
        })),
      );
    }
  };

  const handleSubmit = (data: DemoConfigFormData) => {
    mixpanelUtils.trackFormSubmission(
      "Demo config submit",
      MIXPANEL_SCREENS.DEMO_CONFIGS,
    );
    onSubmit({
      title: data.title,
      config: {
        autoFillJobUrl: data.config.autoFillJobUrl,
        // TODO: Improve this.
        region: data.config.region as DemoRegion,
        // TODO: Improve this.
        position: data.config.position as DemoPosition,
        stevie: {
          client: data.config.stevie.client,
          jobTitle: data.config.stevie.jobTitle,
          jobDescription: data.config.stevie.jobDescription,
          filterQuestionSections: data.config.stevie.filterQuestionSections.map(
            (section) => ({
              title: section.title,
              questions: section.questions.map((question) => question.question),
            }),
          ),
          openEndedQuestion: data.config.stevie.openEndedQuestion,
        },
        hideCandidatesForCompanies: data.config.hideCandidatesForCompanies,
      },
    });
  };

  return (
    <Form {...form}>
      <form
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <fieldset
          className="flex flex-col gap-10"
          disabled={parseMutation.isPending}
        >
          <FormSection
            title="1. Autofill from a Job URL"
            description="Paste a job posting URL to autofill the fields below."
          >
            <FormField
              control={form.control}
              name="config.autoFillJobUrl"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Job Posting URL</FormLabel>
                  <Row className="items-start gap-2">
                    <FormControl>
                      <Input placeholder="https://..." {...field} />
                    </FormControl>
                    <Button
                      disabled={!autoFillJobUrl || parseMutation.isPending}
                      onClick={() => void handleAutoParse()}
                      type="button"
                      variant="outline"
                    >
                      {parseMutation.isPending && (
                        <Loader2Icon className="h-4 w-4 animate-spin" />
                      )}
                      Autofill
                    </Button>
                    <Button
                      onClick={() => {
                        mixpanelUtils.trackButtonClick(
                          "Clear form",
                          MIXPANEL_SCREENS.DEMO_CONFIGS,
                        );
                        form.reset({
                          title: "",
                          config: {
                            autoFillJobUrl: "",
                            region: "",
                            position: "",
                            stevie: {
                              client: "",
                              jobTitle: "",
                              jobDescription: "",
                              filterQuestionSections: [
                                { title: "", questions: [{ question: "" }] },
                              ],
                              openEndedQuestion: "",
                            },
                            hideCandidatesForCompanies: [],
                          },
                        });
                      }}
                      type="button"
                      variant="ghost"
                    >
                      Clear form
                    </Button>
                  </Row>
                  <FormDescription>
                    Existing form entries are not affected.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          <FormDivider />

          <FormSection
            title="2. Basic Information"
            description="High level information about the demo config and its data."
          >
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input placeholder="Title" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="config.region"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Region</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Region" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="text-slate-700">
                      {demoRegionOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Candidate results will be centered around this region in
                    searches.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="config.position"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Position</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Position" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="text-slate-700">
                      {demoPositionOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Candidate results will be centered around this position in
                    searches.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          <FormDivider />

          <FormSection
            title="3. Stevie Configuration"
            description="Stevie AI conversation settings."
          >
            <FormField
              control={form.control}
              name="config.stevie.client"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Client</FormLabel>
                  <FormControl>
                    <Input placeholder="Client" {...field} />
                  </FormControl>
                  <FormDescription>
                    Stevie will say she is working with this client when
                    introducing herself.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="config.stevie.jobTitle"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Job title</FormLabel>
                  <FormControl>
                    <Input placeholder="Job title" {...field} />
                  </FormControl>
                  <FormDescription>
                    Stevie will say she is recruiting for this job title on
                    behalf of the client.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="config.stevie.jobDescription"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Job description</FormLabel>
                  <FormControl>
                    <Textarea
                      className="min-h-[200px]"
                      placeholder="Job description"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Stevie will use this description to answer any questions
                    about the job.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Column className="gap-4">
              <Row className="items-center justify-between">
                <div>
                  <FormLabel className="text-slate-700">
                    Filter Question Sections
                  </FormLabel>
                  <p className="text-sm text-slate-500">
                    At least one question section is required.
                  </p>
                </div>
                <Button
                  onClick={() => {
                    mixpanelUtils.trackButtonClick(
                      "Add section",
                      MIXPANEL_SCREENS.DEMO_CONFIGS,
                    );
                    appendFilterQuestionSection({
                      title: "",
                      questions: [{ question: "" }],
                    });
                  }}
                  type="button"
                  variant="outline"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add section
                </Button>
              </Row>
              {filterQuestionSectionFields.map((section, sectionIndex) => (
                <FilterQuestionSectionInput
                  key={section.id}
                  canRemove={filterQuestionSectionFields.length > 1}
                  control={form.control}
                  onRemove={() => {
                    removeFilterQuestionSection(sectionIndex);
                  }}
                  sectionIndex={sectionIndex}
                />
              ))}
            </Column>
            <FormField
              control={form.control}
              name="config.stevie.openEndedQuestion"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Open-ended question</FormLabel>
                  <FormControl>
                    <Input placeholder="Open-ended question" {...field} />
                  </FormControl>
                  <FormDescription>
                    Stevie will use this question to perform an open-ended deep
                    dive with the candidate.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          <FormDivider />

          <FormSection
            title="4. Hide Candidates for Companies"
            description="Candidates which have worked at the selected companies will be hidden from results."
          >
            <FormField
              control={form.control}
              name="config.hideCandidatesForCompanies"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Hide Candidates for Companies</FormLabel>
                  <FormControl>
                    <Column className="gap-2">
                      {companyChoices.map((company) => (
                        <Row key={company.value} className="items-center gap-2">
                          <Checkbox
                            checked={field.value.includes(company.value)}
                            disabled={!isRegionSelected}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                field.onChange([...field.value, company.value]);
                              } else {
                                field.onChange(
                                  field.value.filter(
                                    (v) => v !== company.value,
                                  ),
                                );
                              }
                            }}
                          />
                          <FormLabel>{company.label}</FormLabel>
                        </Row>
                      ))}
                    </Column>
                  </FormControl>
                  {!isRegionSelected && (
                    <FormDescription>
                      Please select a region first.
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          <Row className="justify-end">
            <Button disabled={isSubmitting} type="submit">
              {submitButtonText}
            </Button>
          </Row>
        </fieldset>
      </form>
    </Form>
  );
};
